import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, saveToken } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, Lock, Users2, Building2, X } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

const ROLE_HOMES = {
  super_admin: "/admin",
  platform_manager: "/admin",
  platform_staff: "/admin",
  platform_observer: "/admin",
  platform_sales: "/admin",
  contractor: "/contractor",
  customer: "/customer",
  admin: "/dashboard",
  pm: "/dashboard",
  designer: "/dashboard",
};

export default function Login() {
  const { user, login, setUser, setWorkspace } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["login", "common"]);
  const [mode, setMode] = useState("email"); // "email" | "mobile"
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const [googleErr, setGoogleErr] = useState(null);

  // If someone is already signed in and lands on the login page, send them
  // straight to their portal.
  useEffect(() => {
    if (user?.role) navigate(ROLE_HOMES[user.role] || "/dashboard", { replace: true });
  }, [user, navigate]);

  // Read `#google_error=<x>` off the URL and store it in state. We use the
  // react-router API (navigate replace) instead of raw history.replaceState —
  // otherwise BrowserRouter re-syncs the browser URL back to the hashed
  // location on the next render, undoing our cleanup.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (!hash.includes("google_error=")) return;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const err = params.get("google_error");
    if (err) {
      setGoogleErr(err);
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  // Fire the toast off state (not directly inside the hash-reading effect)
  // so it survives StrictMode's double-mount and any Toaster mount races.
  useEffect(() => {
    if (!googleErr) return;
    // Map backend error code → i18n key. If a code isn't in the map, fall
    // back to the raw string.
    const keyMap = {
      not_invited: "toasts.google.notInvited",
      access_denied: "toasts.google.accessDenied",
      email_unverified: "toasts.google.emailUnverified",
      email_missing: "toasts.google.emailMissing",
      invalid_state: "toasts.google.invalidState",
      missing_code: "toasts.google.missingCode",
      token_exchange_failed: "toasts.google.tokenExchangeFailed",
      userinfo_failed: "toasts.google.userinfoFailed",
      no_access_token: "toasts.google.noAccessToken",
    };
    const key = keyMap[googleErr];
    toast.error(key ? t(key) : decodeURIComponent(googleErr));
    setGoogleErr(null);
  }, [googleErr, t]);

  const goByRole = (r) => navigate(ROLE_HOMES[r] || "/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "email") {
        const data = await login(email.trim(), password);
        toast.success(t("toasts.welcomeBack", { name: data.user.name }));
        goByRole(data.user.role);
      } else {
        const { data } = await api.post("/auth/contractor-login", {
          phone: phone.trim(),
          password,
        });
        if (data.token) saveToken(data.token);
        setUser(data.user);
        setWorkspace(data.workspace || null);
        toast.success(t("toasts.welcomeBack", { name: data.user.name }));
        goByRole(data.user.role);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Hit OUR backend which owns the custom GCP OAuth client — the Google
    // consent screen will show "Continue to orkaiflow.com" (our verified
    // domain) instead of "emergent.com".
    const backend = process.env.REACT_APP_BACKEND_URL;
    const target = "/dashboard";
    window.location.href = `${backend}/api/auth/google/start?redirect=${encodeURIComponent(target)}`;
  };

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-5">
      {/* -------- LEFT (story / hero) -------- */}
      <aside className="lg:col-span-3 flex flex-col lg:justify-between p-6 sm:p-10 lg:p-16 bg-[#FDFBF7]">
        <div className="flex items-center gap-3">
          <img src="/logo-mark.png" alt="OrkaiFlow" className="w-12 h-12 object-contain" />
          <span className="font-display text-2xl font-bold text-[#2C2A29] tracking-tight">OrkaiFlow</span>
        </div>

        <div className="max-w-xl py-8 lg:py-0">
          <h1 className="font-display font-bold text-[#2C2A29] tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] [text-wrap:balance]">
            {t("login:hero.line1")}
            <br className="hidden sm:inline" /> {t("login:hero.line1Break")}
          </h1>
          <h2 className="font-display font-bold text-[#E07A5F] tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] mt-3 [text-wrap:balance]">
            {t("login:hero.line2")}
            <br className="hidden sm:inline" /> {t("login:hero.line2Break")}
          </h2>
          <p className="mt-8 text-base sm:text-lg text-[#7A7571] leading-relaxed max-w-lg [text-wrap:pretty]">
            {t("login:hero.body")}
          </p>
          <p className="mt-8 italic text-sm text-[#7A7571] leading-relaxed max-w-lg border-l-2 border-[#E07A5F] pl-4 [text-wrap:pretty]">
            {t("login:hero.quote")}
          </p>
        </div>

        {/* Desktop-only footer inside the hero column */}
        <footer className="hidden lg:block text-xs text-[#7A7571] space-y-2 pt-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{t("common:footer.copyright")}</span>
            <Link to="/terms" className="hover:text-[#2C2A29]" data-testid="footer-terms">{t("common:footer.terms")}</Link>
            <Link to="/privacy" className="hover:text-[#2C2A29]" data-testid="footer-privacy">{t("common:footer.privacy")}</Link>
            <Link to="/security" className="hover:text-[#2C2A29]" data-testid="footer-security">{t("common:footer.security")}</Link>
            <a href="https://status.orkaiflow.com" className="hover:text-[#2C2A29]" data-testid="footer-status">{t("common:footer.status")}</a>
            <a href="mailto:support@orkaiflow.com" className="hover:text-[#2C2A29]" data-testid="footer-support">{t("common:footer.support")}</a>
            <LanguageSwitcher variant="footer" />
          </div>
          <div className="text-[#B8B0A6]">
            {t("common:footer.tagline")}
          </div>
        </footer>
      </aside>

      {/* -------- RIGHT (sign-in card) -------- */}
      <section className="lg:col-span-2 flex items-center justify-center p-6 sm:p-8 lg:p-10 bg-white lg:border-l lg:border-[#EBE5D9]">
        <div className="w-full max-w-md space-y-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#2C2A29]">{t("login:card.welcome")}</h2>
            <p className="text-[#7A7571] mt-1">{t("login:card.subtitle")}</p>
          </div>

          {/* Continue with Google — keeps the customer/contractor Google path
              available without cluttering the primary email form. */}
          <button
            type="button"
            onClick={startGoogle}
            data-testid="login-google-btn"
            className="w-full inline-flex items-center justify-center gap-3 border border-[#EBE5D9] hover:border-[#2C2A29] hover:bg-[#FDFBF7] rounded-2xl px-5 py-3 font-semibold text-[#2C2A29] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 34.8 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4 5.6l6.5 5.5C41.1 36.4 44 30.7 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            {t("login:card.continueWithGoogle")}
          </button>

          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#B8B0A6]">
            <span className="flex-1 h-px bg-[#EBE5D9]" />
            {t("login:card.orSignInWith")}
            <span className="flex-1 h-px bg-[#EBE5D9]" />
          </div>

          {/* Email / Mobile mode toggle */}
          <div role="tablist" aria-label="Sign-in method" className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-[#F7F4EC]">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "email"}
              data-testid="login-mode-email"
              onClick={() => setMode("email")}
              className={`py-2 rounded-xl text-sm font-semibold transition-colors ${mode === "email" ? "bg-white text-[#2C2A29] shadow-sm" : "text-[#7A7571] hover:text-[#2C2A29]"}`}
            >
              {t("login:card.modeEmail")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "mobile"}
              data-testid="login-mode-mobile"
              onClick={() => setMode("mobile")}
              className={`py-2 rounded-xl text-sm font-semibold transition-colors ${mode === "mobile" ? "bg-white text-[#2C2A29] shadow-sm" : "text-[#7A7571] hover:text-[#2C2A29]"}`}
            >
              {t("login:card.modeMobile")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "email" ? (
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{t("login:card.labelEmail")}</label>
                <input
                  data-testid="login-email"
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-soft" placeholder={t("login:card.placeholderEmail")}
                />
              </div>
            ) : (
              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">{t("login:card.labelMobile")}</label>
                <input
                  data-testid="login-phone"
                  type="tel" required autoComplete="tel"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="input-soft" placeholder={t("login:card.placeholderMobile")}
                />
                <p className="text-[11px] text-[#7A7571] mt-2">
                  <Trans i18nKey="login:card.mobileHint" components={{ 1: <span className="font-mono" /> }} />
                </p>
              </div>
            )}
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="text-[11px] uppercase tracking-widest font-bold text-[#7A7571]">{t("login:card.labelPassword")}</label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="login-forgot"
                  className="text-xs text-[#E07A5F] font-semibold hover:underline"
                >
                  {t("login:card.forgotPassword")}
                </button>
              </div>
              <input
                data-testid="login-password"
                type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-soft" placeholder={t("login:card.placeholderPassword")}
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? t("common:actions.signingIn") : (<>{t("common:actions.continue")} <ArrowRight size={16} /></>)}
            </button>
          </form>

          {/* Secure Login band */}
          <div className="pt-6 border-t border-[#EBE5D9]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-3 flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-[#166534]" /> {t("login:card.secureLogin")}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[#7A7571]">
              <li className="inline-flex items-center gap-1.5"><Lock size={12} className="text-[#166534] flex-shrink-0" /> {t("login:card.secureEncrypted")}</li>
              <li className="inline-flex items-center gap-1.5"><Users2 size={12} className="text-[#166534] flex-shrink-0" /> {t("login:card.secureRoles")}</li>
              <li className="inline-flex items-center gap-1.5"><Building2 size={12} className="text-[#166534] flex-shrink-0" /> {t("login:card.secureCompany")}</li>
            </ul>
          </div>

          {/* Bottom help */}
          <div className="text-xs text-[#7A7571] text-center pt-2 leading-relaxed">
            {t("login:card.needHelp")} <span className="text-[#2C2A29] font-semibold">{t("login:card.contactCompany")}</span>
            <br /><a href="mailto:support@orkaiflow.com" className="text-[#E07A5F] font-semibold hover:underline" data-testid="help-support">{t("login:card.contactSupport")}</a>
          </div>

          <div className="text-center text-xs text-[#B8B0A6] pt-2">
            {t("login:card.noWorkspace")} <Link to="/register" className="text-[#E07A5F] font-semibold hover:underline" data-testid="login-register-link">{t("login:card.createOne")}</Link>
          </div>
        </div>
      </section>

      {/* Mobile-only footer — sits at the bottom of the page below the sign-in form */}
      <footer className="lg:hidden text-xs text-[#7A7571] space-y-2 px-6 py-6 bg-white border-t border-[#EBE5D9]">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>{t("common:footer.copyright")}</span>
          <Link to="/terms" className="hover:text-[#2C2A29]" data-testid="footer-terms-mobile">{t("common:footer.terms")}</Link>
          <Link to="/privacy" className="hover:text-[#2C2A29]" data-testid="footer-privacy-mobile">{t("common:footer.privacy")}</Link>
          <Link to="/security" className="hover:text-[#2C2A29]" data-testid="footer-security-mobile">{t("common:footer.security")}</Link>
          <a href="https://status.orkaiflow.com" className="hover:text-[#2C2A29]" data-testid="footer-status-mobile">{t("common:footer.status")}</a>
          <a href="mailto:support@orkaiflow.com" className="hover:text-[#2C2A29]" data-testid="footer-support-mobile">{t("common:footer.support")}</a>
          <LanguageSwitcher variant="footer" />
        </div>
        <div className="text-[#B8B0A6]">
          {t("common:footer.tagline")}
        </div>
      </footer>

      {/* Forgot password modal — we don't run a self-serve reset flow.
          Direct users to their ID firm admin (who can reset via Team page)
          or to Support. */}
      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} />}
    </div>
  );
}

function ForgotPasswordModal({ onClose }) {
  const { t } = useTranslation(["login", "common"]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="forgot-password-modal"
    >
      <div
        className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#7A7571] hover:text-[#2C2A29]"
          aria-label={t("common:actions.close")}
          data-testid="forgot-password-close"
        >
          <X size={18} />
        </button>
        <h3 className="font-display text-2xl font-bold text-[#2C2A29] mb-2">{t("login:forgot.title")}</h3>
        <p className="text-sm text-[#7A7571] mb-5 leading-relaxed">
          {t("login:forgot.intro")}
        </p>
        <div className="space-y-3">
          <div className="rounded-2xl border border-[#EBE5D9] bg-[#FDFBF7] p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">{t("login:forgot.staffLabel")}</div>
            <p className="text-sm text-[#2C2A29]">
              {t("login:forgot.staffBody")}
            </p>
          </div>
          <div className="rounded-2xl border border-[#EBE5D9] bg-[#FDFBF7] p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-1">{t("login:forgot.googleLabel")}</div>
            <p className="text-sm text-[#2C2A29]">
              <Trans i18nKey="login:forgot.googleBody" components={{ 1: <span className="font-semibold" /> }} />
            </p>
          </div>
        </div>
        <a
          href="mailto:support@orkaiflow.com"
          className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2"
          data-testid="forgot-password-support"
        >
          {t("login:card.contactSupport")}
        </a>
      </div>
    </div>
  );
}
